import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchTriggerStatus } from "@me8eon/ai.tools.nextthink";
import { useJwtToken } from "@me8eon/react_authentication";


export type AssistantTask = {
    requestId: string;
    task: string;
    status: string;
    timestamp?: number;
}

interface IContextValues {
    assistanceTasks: AssistantTask[];
    addAssistantTask: (task: AssistantTask) => void;
    manualRecommendedActions: string[];
    addManualRecommendedActions: (actions: string[]) => void;
    removeManualActions: () => void;
    removeOldAssistanceTask: () => void;
    fetchStatusForInProgressTasks: () => void;
}

const AiAssistantContext = createContext<IContextValues>({
    addAssistantTask: () => { },
    removeOldAssistanceTask: () => { },
    assistanceTasks: [],
    manualRecommendedActions: [],
    addManualRecommendedActions: (actions: string[]) => { },
    removeManualActions: () => { },
    fetchStatusForInProgressTasks: () => { },
});

const AiAssistantContextProvider = ({ children }: { children: ReactNode }) => {
    const jwtTokenFn = useJwtToken();
    const [assistanceTasks, setAssistanceTasks] = useState(() => {
        return JSON.parse(localStorage.getItem("latestTasks") || "[]");
    });
    const [manualRecommendedActions, setManualRecommendedActions] = useState<string[]>(() => {
        return JSON.parse(localStorage.getItem("manualRecommendedActions") || "[]");
    });

    useEffect(() => {
        if (assistanceTasks.some((task: AssistantTask) => task.status === "progress")) {
            fetchStatusForInProgressTasks();
        }
    }, []); // Run once on mount

    const fetchStatusForInProgressTasks = async () => {
        const storedTasks: AssistantTask[] = JSON.parse(localStorage.getItem("latestTasks") || "[]");
        let hasUpdates = false;
        const updatedTasks = await Promise.all(
            storedTasks.map(async (task) => {
                if (task.status === "progress") {
                    const { data } = await fetchTriggerStatus(task.requestId, jwtTokenFn);
                    if (data.length && data[0]["remote_action.execution.status"]) {
                        const status = !['success', 'failure', 'progress']
                            .includes(data[0]["remote_action.execution.status"])
                            ? 'failure'
                            : data[0]["remote_action.execution.status"];
                        if (task.status !== status) {
                            hasUpdates = true;
                            return { ...task, status };
                        }
                    }
                }
                return task;
            }),
        );
        // Only update state and localStorage if there was an update
        if (hasUpdates) {
            setAssistanceTasks(updatedTasks);
            localStorage.setItem("latestTasks", JSON.stringify(updatedTasks));
        }
    };

    const addAssistantTask = (task: AssistantTask) => {
        let localTasks: AssistantTask[] = JSON.parse(localStorage.getItem("latestTasks") || "[]");
        const taskMap = new Map(localTasks.map(item => [item.requestId, item]));
        const existingTask = taskMap.get(task.requestId);
        if (existingTask) {
            // Merge only changed fields
            const mergedTask = { ...existingTask, ...task };
            taskMap.set(task.requestId, mergedTask);
        } else {
            taskMap.set(task.requestId, task);
        }
        const updatedTasks = Array.from(taskMap.values());
        localStorage.setItem("latestTasks", JSON.stringify(updatedTasks));
        setAssistanceTasks(updatedTasks);
    };

    const removeOldAssistanceTask = useCallback(() => {
        try {
            const storedTasks: AssistantTask[] = JSON.parse(localStorage.getItem("latestTasks") || "[]");
            const now = Date.now();

            const updatedTasks = storedTasks.filter(task => {
                if (task.timestamp) {
                    const taskTime = new Date(task.timestamp).getTime();
                    const diffInMinutes = Math.abs(now - taskTime) / 1000 / 60;
                    return diffInMinutes < 20; // Keep tasks from the last 20 minutes
                }
                return false;
            });

            localStorage.setItem("latestTasks", JSON.stringify(updatedTasks));
            setAssistanceTasks(updatedTasks);
        } catch (error) {
            console.error("Failed to clean up tasks from localStorage:", error);
        }
    }, []);

    const addManualRecommendedActions = useCallback((actions: string[]) => {
        const updatedTasks = [...new Set([...manualRecommendedActions, ...actions])];
        localStorage.setItem("manualRecommendedActions", JSON.stringify(updatedTasks));
        setManualRecommendedActions(updatedTasks);
    }, []);

    const removeManualActions = useCallback(() => {
        localStorage.removeItem("manualRecommendedActions");
        setManualRecommendedActions([]);
    }, []);

    return (
        <AiAssistantContext.Provider
            value={{
                assistanceTasks,
                addAssistantTask,
                manualRecommendedActions,
                addManualRecommendedActions,
                removeManualActions,
                removeOldAssistanceTask,
                fetchStatusForInProgressTasks,
            }}>
            {children}
        </AiAssistantContext.Provider>
    );
};

const useAiAssistantContext = () => {
    const context = React.useContext(AiAssistantContext);
    if (!context) {
        throw new Error("useAiAssistantContext must be used within an AiAssistantContextProvider");
    }
    return context;
}

export { AiAssistantContext, AiAssistantContextProvider, useAiAssistantContext };
